import { Box } from "@mantine/core";
import { Map } from 'react-map-gl';
import { FC, useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import FreehandMode from './../FreeHandMode';
import useViewportControl from 'hooks/useViewportControl';

import styles from './../../map.module.scss'
import {MapPinsSourceAndLayers, MapRoutePinsSourceAndLayers, MapRouteSourceAndLayers, MapStartEndLocationSourceAndLayers, SearchResultPinsSourceAndLayers, SelectedPinOverlay} from "./MapSource";
import { ReactComponent as LassoIcon } from 'assets/lasso.svg';
import { ReactComponent as LocationIcon } from 'assets/location.svg';
import { ReactComponent as ListBulletIcon } from 'assets/ListBullets.svg';
import { ReactComponent as RoutesIcon } from 'assets/routes.svg';
import { ReactComponent as ArrowLeftIcon } from 'assets/arrow_left_alt.svg';
import * as turf from '@turf/turf';
import { DEFAULT_PIN_COLOR, Pin } from "Modules/Map";

const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
const CENTER_FOCAL_ZOOM = 15;

export interface MapState {
  latLon: [number, number]
  focalZoom: number;
}

export interface MapCanvasHandle {
  getViewBoxPins: () => Pin[] | null;
  // returns: "lat,lon"
  getViewBoxCenter: () => string | null;
  getViewBoxRadius: () => number | null;
  centerMap: (latLon: [number, number]) => void;
};

interface Props {
  onPinSelected: (id: number| string | null) => void;
  onPinsLassoed: (id: (number|string)[] | null) => void;
  onViewPins: (ids: (string|number)[]) => void;
  onSlideUpMenuOpen: () => void;
  onNavigateHome: () => void;
  onMapLoaded: () => void;
  mapState: MapState;
  pins: Pin[];
  searchResultPins: Pin[];
  routeData?: number[][];
  routePins?: {
    id: number | string;
    latLon: [number, number];
  }[];
  routeStartEndPins?: {
    id: number | string;
    latLon: [number, number];
  }[];
  selectedPinId?: number;
  isColorLabelActive: boolean;
  isColorLabelOnView: boolean;
  onColorLabeOnViewChange: (v: boolean) => void;
  onViewBoxChange: () => void;
  isModalOpen?: boolean;
}
const MapCanvas = forwardRef<MapCanvasHandle, Props>(({
  pins,
  searchResultPins,
  onPinSelected,
  onViewPins,
  onPinsLassoed,
  onMapLoaded,
  mapState,
  onSlideUpMenuOpen,
  onNavigateHome,
  routeData,
  routePins,
  routeStartEndPins,
  isColorLabelOnView,
  isColorLabelActive,
  onColorLabeOnViewChange,
  selectedPinId,
  onViewBoxChange,
  isModalOpen = false
}, ref) => {
  const mapRef = useRef<mapboxgl.Map | undefined>();
  const [isMapReady, setIsMapReady] = useState(false);
  const [pinsGeoJSON, setPinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: string; selected: boolean }>>();
  const [searchResultPinsGeoJSON, setSearchResultPinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: string; selected: boolean }>>();
  const [routeGeoJSON, setRouteGeoJSON] = useState<GeoJSON.Feature<GeoJSON.LineString>
   | null>(null);
  const [routePinsGeoJSON, setRoutePinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: number | string; }>>();
  const [routeStartEndPinsGeoJSON, setRouteStartEndPinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: string; type: "start" | "end" }>>();
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const geolocateControlRef = useRef<mapboxgl.GeolocateControl | null>(null);
  const [isLassoMode, setIsLassoMode] = useState(false);
  const drawRef = useRef<any>(null);
  const pinsRef = useRef<Pin[]>();
  pinsRef.current = pins;

  // Control mobile viewport zoom settings - disable zoom in map view
  // BUT enable zoom when modal (baseball card) is open
  useViewportControl(true, isModalOpen);

  const getViewBoxPins = (): Pin[] | null => {
    if (!mapRef.current) return null;
    const bounds = mapRef.current.getBounds();
    if (!bounds) return null;

    const viewBoxPins = pins.filter(
      (p) => bounds.contains([p.latLon[1], p.latLon[0]])
    );
    return viewBoxPins;
  }

  const centerMap = (latLon: [number, number]) => {
    if (!mapRef.current) return null;
    mapRef.current.flyTo({
      center: [latLon[1], latLon[0]],
      zoom: CENTER_FOCAL_ZOOM
    });
  }

  const getViewBoxCenter = (): string | null => {
    if (!mapRef.current) return null;
      const bounds = mapRef.current.getBounds();
    if (!bounds) return null;
    return `${bounds.getCenter().lat}, ${bounds?.getCenter().lng}`;
  };

  const getViewBoxRadius = (): number | null => {
    if (!mapRef.current) return null;
      const bounds = mapRef.current.getBounds();
    if (!bounds) return null;
    const center = turf.point(
      [bounds.getCenter().lng, bounds.getCenter().lat]
    );
    const top = turf.point(
      [bounds.getCenter().lng, bounds.getNorth()]
    )
    return turf.distance(center, top, { units: "meters"});
  }

  const centralizeMap = () => {
    if (geolocateControlRef.current) {
      geolocateControlRef.current.trigger();
    }
  };

  const startDrawing = () => {
    if (!mapRef.current || !drawRef.current) {
      console.warn('Map or draw controls not initialized');
      return;
    }

    drawRef.current.deleteAll();

    if (!isLassoMode) {
      drawRef.current.changeMode('draw_polygon');
    } else {
      drawRef.current.changeMode('simple_select');
    }

    setIsLassoMode(!isLassoMode);
  };

  const updateArea = (e: any) => {
    const data = drawRef.current.getAll();

    if (data.features.length > 0) {
      const polygon = data.features[0];
      const lassoedPinsIds: (number|string)[] = [];

      pinsRef.current?.forEach((p) => {
        const [lat, lon] = p.latLon;

        const point = turf.point([Number(lon), Number(lat)]);
        const isInside = turf.booleanPointInPolygon(point, polygon as any);

        if (isInside) {
          lassoedPinsIds.push(p.id);
        }

      });
      onPinsLassoed(lassoedPinsIds);
      setIsLassoMode(false);
    } else {
      if (e.type === 'draw.delete') {
        onPinsLassoed(null)
      }
    }
  }


  const mountMapboxDraw = () => {
    if (!mapRef.current) return;

    drawRef.current = new MapboxDraw({
      controls: {
        polygon: false,
        trash: false,
        point: false,
        combine_features: false,
        uncombine_features: false,
        line_string: false,
      },
      modes: {
        ...MapboxDraw.modes,
        draw_polygon: FreehandMode,
      },
    });
    mapRef.current.addControl(drawRef.current);
    const map = mapRef.current;
    map.on('draw.create', updateArea);
    map.on('draw.delete', updateArea);
    map.on('draw.update', updateArea);
    return () => {
      if (mapRef.current && drawRef.current) {
        const map = mapRef.current;
        map.off('draw.create', updateArea);
        map.off('draw.delete', updateArea);
        map.off('draw.update', updateArea);
        map.removeControl(drawRef.current);
      }
    };
  };

  useImperativeHandle(ref, () => ({
    getViewBoxPins,
    getViewBoxCenter,
    getViewBoxRadius,
    centerMap,
  }));

  useEffect(() => {
    // COMPLETE ZOOM PREVENTION: Disable ALL browser zoom while map view is active
    console.log('Map mounted: Disabling all browser zoom');

    document.body.style.overscrollBehavior = 'none';
    if (mapRef.current) {
      mapRef.current.getCanvas().style.touchAction = 'none';
    }

    // 1. Prevent keyboard zoom (Cmd/Ctrl + Plus/Minus/0)
    const preventKeyboardZoom = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0' || e.key === '=')) {
        e.preventDefault();
      }
    };

    // 2. Prevent ALL trackpad/mouse wheel zoom globally
    const preventWheelZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    // 3. Prevent gesture zoom (Safari)
    const preventGestureZoom = (e: Event) => {
      e.preventDefault();
    };

    // Add global listeners
    document.addEventListener('keydown', preventKeyboardZoom);
    document.addEventListener('wheel', preventWheelZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestureZoom);
    document.addEventListener('gesturechange', preventGestureZoom);
    document.addEventListener('gestureend', preventGestureZoom);

    // Cleanup on unmount - restore normal zoom behavior
    return () => {
      console.log('Map unmounted: Re-enabling browser zoom');
      document.removeEventListener('keydown', preventKeyboardZoom);
      document.removeEventListener('wheel', preventWheelZoom);
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);
      document.body.style.overscrollBehavior = 'auto';
    };
  }, []);

  // Additional touch handling for map interactions (simplified since zoom is globally disabled)
  useEffect(() => {
    // Prevent iOS zoom on double-tap
    let lastTouchEnd = 0;
    let touchStartTime = 0;
    let initialTouchDistance = 0;

    const preventZoom = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
        e.stopPropagation();
      }
      lastTouchEnd = now;
    };

    // Enhanced pinch zoom prevention that tracks gesture intent
    const handleTouchStart = (e: TouchEvent) => {
      touchStartTime = Date.now();

      // If multi-touch (pinch gesture starting)
      if (e.touches.length === 2) {
        // Check if ANY touch is near the map (even if one is outside)
        const mapRect = mapRef.current?.getContainer()?.getBoundingClientRect();
        if (mapRect) {
          const expandedBounds = 100; // More generous for touch

          // Check if either touch point is near the map
          const touch1Near = (
            e.touches[0].clientX >= (mapRect.left - expandedBounds) &&
            e.touches[0].clientX <= (mapRect.right + expandedBounds) &&
            e.touches[0].clientY >= (mapRect.top - expandedBounds) &&
            e.touches[0].clientY <= (mapRect.bottom + expandedBounds)
          );

          const touch2Near = (
            e.touches[1].clientX >= (mapRect.left - expandedBounds) &&
            e.touches[1].clientX <= (mapRect.right + expandedBounds) &&
            e.touches[1].clientY >= (mapRect.top - expandedBounds) &&
            e.touches[1].clientY <= (mapRect.bottom + expandedBounds)
          );

          // If either touch is near map, prevent browser zoom
          if (touch1Near || touch2Near) {
            e.preventDefault();
          }
        } else {
          // Fallback: always prevent on multi-touch
          e.preventDefault();
        }

        // Calculate initial distance between touches
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        initialTouchDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
      }
      // Single touch - might be start of tap or pan
      else if (e.touches.length === 1) {
        // Don't prevent default yet - let map handle panning
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // For pinch gestures, always prevent viewport zoom
      if (e.touches.length === 2) {
        e.preventDefault();

        // Let Mapbox handle the actual map zoom
        // but prevent iOS from zooming the viewport
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        // Optional: Add custom zoom handling if needed
        // const zoomDelta = currentDistance - initialTouchDistance;
      }
    };

    // Chrome desktop: Prevent trackpad pinch zoom
    const handleWheel = (e: WheelEvent) => {
      // Chrome fires wheel events with ctrlKey=true for pinch gestures on trackpad
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        // The map will still zoom via Mapbox's own handlers
        // but we prevent the browser viewport zoom
      }
    };

    const mapContainer = mapRef.current?.getContainer();
    if (mapContainer) {
      // Add touch event listeners with passive: false to allow preventDefault
      mapContainer.addEventListener('touchend', preventZoom, { passive: false });
      mapContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
      mapContainer.addEventListener('touchmove', handleTouchMove, { passive: false });

      // Chrome desktop: Prevent trackpad pinch from zooming browser
      mapContainer.addEventListener('wheel', handleWheel, { passive: false });

      // Gesture events for Safari - these are critical for preventing viewport zoom
      const preventGesture = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      };

      // These events are Safari-specific and fire for pinch gestures
      document.addEventListener('gesturestart', preventGesture, { passive: false });
      document.addEventListener('gesturechange', preventGesture, { passive: false });
      document.addEventListener('gestureend', preventGesture, { passive: false });

      // Note: Document-level wheel zoom is now handled globally in the first useEffect
      // This local handler is kept for potential future use but currently redundant

      return () => {
        mapContainer.removeEventListener('touchend', preventZoom);
        mapContainer.removeEventListener('touchstart', handleTouchStart);
        mapContainer.removeEventListener('touchmove', handleTouchMove);
        mapContainer.removeEventListener('wheel', handleWheel);
        document.removeEventListener('gesturestart', preventGesture);
        document.removeEventListener('gesturechange', preventGesture);
        document.removeEventListener('gestureend', preventGesture);
      };
    }
  }, [mapRef.current]);

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      map.on('draw.modechange', (e) => {
        const mapCanvas = map.getCanvas();
        // If entering a draw mode
        if ((e as any).mode.startsWith('draw')) {
          document.body.style.overscrollBehavior = 'none';
          mapCanvas.style.touchAction = 'none';
        } else {
          // Reset when exiting draw mode
          document.body.style.overscrollBehavior = 'auto';
          mapCanvas.style.touchAction = 'auto';
          drawRef.current.deleteAll();
        }
      });
    }
  }, [mapRef.current]);

  useEffect(() => {
    if (routeData) {
      const g: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: routeData,
        }
      };
      setRouteGeoJSON(g);
    } else {
      setRouteGeoJSON(null);
    }
  }, [routeData]);

  useEffect(() => {
    if (mapRef.current && isMapReady && !drawRef.current) {
      mountMapboxDraw();

      // Add GeolocateControl once when map is loaded
      if (!geolocateControlRef.current) {
        geolocateControlRef.current = new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
          showUserHeading: true,
        });

        mapRef.current.addControl(geolocateControlRef.current, 'top-left');
        // NOTE: this is more reliable to hide the standard map control location
        geolocateControlRef.current._container.style.display = 'none';

        setTimeout(() => {
          const geolocateButton = document.querySelector('.mapboxgl-ctrl-geolocate');
          if (geolocateButton) {
            (geolocateButton as HTMLElement).style.display = 'none';
            (geolocateButton as HTMLElement).style.position = 'absolute';
          }
        }, 0);
      }
    }
  }, [mapRef.current, isMapReady]);

  useEffect(() => {
    const features: GeoJSON.Feature<GeoJSON.Point, { id: string; selected: boolean, color: string }>[] = pins.map((p) => {
      const [lat, lon] = p.latLon;
      return {
        type: 'Feature',
        properties: {
          id: p.id as any,
          selected: false,
          color: p?.color ? p.color : DEFAULT_PIN_COLOR,
        },
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      };
    });
    setPinsGeoJSON({
      type: "FeatureCollection",
      features
    });
  }, [pins]);

  useEffect(() => {
    const features: GeoJSON.Feature<GeoJSON.Point, { id: string; selected: boolean, color: string }>[] = searchResultPins.map((p) => {
      const [lat, lon] = p.latLon;
      return {
        type: 'Feature',
        properties: {
          id: p.id as any,
          selected: false,
          color: p?.color ? p.color : DEFAULT_PIN_COLOR,
        },
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      };
    });
    setSearchResultPinsGeoJSON({
      type: "FeatureCollection",
      features
    });
  }, [searchResultPins]);

  useEffect(() => {
    if (routePins) {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: number | string }>[] = [];
      routePins
        .forEach((p) => {
          const lon = p.latLon[1];
          const lat = p.latLon[0];
          features.push({
            type: "Feature",
            properties: {
              id: p.id,
            },
            geometry: {
              type: 'Point',
              coordinates: [lon, lat]
            }
          })
        });

      const ds: GeoJSON.FeatureCollection<GeoJSON.Point, { id: number | string }> = {
        type: "FeatureCollection",
        features
      }
      setRoutePinsGeoJSON(ds);
    } else {
      setRoutePinsGeoJSON(undefined);
    }
  }, [routePins]);

  useEffect(() => {
    if (routeStartEndPins) {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: string; type: "start" | "end" }>[] = [];
      if (routeStartEndPins.length > 0) {
        const lat = routeStartEndPins[0].latLon[0];
        const lon = routeStartEndPins[0].latLon[1];
        features.push({
          type: 'Feature',
          properties: {
            id: `start`,
            type: 'start'
          },
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          }
        });
        if (routeStartEndPins.length > 1) {
          const lat = routeStartEndPins[1].latLon[0];
          const lon = routeStartEndPins[1].latLon[1];
          features.push({
            type: 'Feature',
            properties: {
              id: `end`,
              type: 'end'
            },
            geometry: {
              type: 'Point',
              coordinates: [lon, lat]
            }
          });
        }
      }

      const ds: GeoJSON.FeatureCollection<GeoJSON.Point, { id: string; type: "start" | "end" }> = {
        type: "FeatureCollection",
        features
      }
      setRouteStartEndPinsGeoJSON(ds);
    } else {
      setRouteStartEndPinsGeoJSON(undefined);
    }
  }, [routeStartEndPins]);

  return (
    <Box style={{zIndex: 1000}}>
      <Map
        ref={mapRef as any}
        style={{
          position: 'absolute',
          width: '100vw',
          height: '100%',
          top: '0',
          left: '0'
        }}
        touchPitch={false}
        dragRotate={false}
        touchZoomRotate={{
          disableRotation: true
        }}
        onTouchStart={(e) => {
          setTouchStart({
            x: e.point.x,
            y: e.point.y,
            time: Date.now()
          });
        }}
        onTouchEnd={(e) => {
          if (!touchStart) return;

          const touchEnd = {
            x: e.point.x,
            y: e.point.y,
            time: Date.now()
          };

          const distance = Math.sqrt(
            Math.pow(touchEnd.x - touchStart.x, 2) +
            Math.pow(touchEnd.y - touchStart.y, 2)
          );
          const duration = touchEnd.time - touchStart.time;

          if (duration < 300 && distance < 10) {
            const [featureEvent,] = e.target.queryRenderedFeatures(e.point);

            if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
              e.preventDefault();
              const clusterId = featureEvent.properties?.cluster_id;
              const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

              if (source && clusterId !== undefined && mapRef.current) {
                source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                  if (err || !mapRef.current) return;

                  const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
                  const targetZoom = Math.max(12, (zoom || 10) + 1);

                  mapRef.current.flyTo({
                    center: [coordinates[0], coordinates[1]],
                    zoom: targetZoom,
                    speed: 2,
                    curve: 1.5,
                    bearing: 0,
                    pitch: 0,
                    essential: true,
                  });
                });
              }
              return;
            }

            onPinSelected(null);

            if (featureEvent && (featureEvent.layer?.id === "unclustered-point" || featureEvent.layer?.id === "route-points"
              || featureEvent.layer?.id === "search-result-point"
            )) {
              onPinSelected(featureEvent?.properties?.id)
            } else {
              onPinSelected(null);
            }
          }
          onViewBoxChange();
          setTouchStart(null);
        }}
        onClick={(e) => {
          const [featureEvent,] = e.target.queryRenderedFeatures(e.point);
          if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
            e.preventDefault();
            const clusterId = featureEvent.properties?.cluster_id;
            const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

            if (source && clusterId !== undefined && mapRef.current) {
              source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err || !mapRef.current) return;

                const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
                const targetZoom = Math.max(12, (zoom || 10) + 1);

                mapRef.current.flyTo({
                  center: [coordinates[0], coordinates[1]],
                  zoom: targetZoom,
                  speed: 2,
                  curve: 1.5,
                  bearing: 0,
                  pitch: 0,
                  essential: true,
                });
              });
            }
            return;
          }

          // Only reset state if not clicking on cluster
          onPinSelected(null);
          if (featureEvent && ((featureEvent.layer?.id === "unclustered-point") || (featureEvent.layer?.id === "route-points") 
          || (featureEvent.layer?.id === "search-result-point"))) {
            onPinSelected(featureEvent?.properties?.id);
          } else {
            onPinSelected(null);
          }
        }}
        onLoad={() => {
          mapRef?.current?.loadImage("/icons/red-marker.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('custom-marker', img, { sdf: true });
          });
          mapRef?.current?.loadImage("/icons/route-marker.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('custom-marker-route', img);
          });
          mapRef?.current?.loadImage("/icons/selected-marker-green.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('selected-marker-green', img);
          });
          mapRef?.current?.loadImage("/icons/search-marker.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('custom-search-marker', img);
          });

          onMapLoaded();
          setIsMapReady(true);
        }}
        initialViewState={{
          longitude: mapState.latLon[1],
          latitude: mapState.latLon[0],
          zoom: mapState.focalZoom,
          bearing: 0,
          pitch: 0,
        }}
        onData={(e: any) => {
          if (e?.sourceId === "practitioners-source") {
            const map = mapRef.current;
            const features = map?.querySourceFeatures("practitioners-source", {
              sourceLayer: "unclustered-points",
            });
            const idsToFetch: number[] = [];
            features?.forEach((f) => {
              if (!f?.properties?.point_count) {

                if (f?.properties?.id && !idsToFetch.includes(f?.properties?.id)) {
                  idsToFetch.push(f.properties.id);
                }
              }
            });
            if (idsToFetch.length > 0) {
              onViewPins(idsToFetch);
            }
          }
        }}
        mapStyle="mapbox://styles/mapbox/standard"
        mapboxAccessToken={mapboxToken}
      >
        {isMapReady && (
          <MapPinsSourceAndLayers
            id="practitioners-source"
            selectedPinId={selectedPinId}
            type="geojson"
            data={pinsGeoJSON}
            cluster={true}
            clusterMaxZoom={8}
            clusterRadius={50} />
        )}
        {isMapReady && routeGeoJSON && (
          <MapRouteSourceAndLayers
            id="route"
            type="geojson"
            data={routeGeoJSON} />
        )}
        {isMapReady && routePinsGeoJSON && (
          <MapRoutePinsSourceAndLayers
            id="route-source"
            type="geojson"
            cluster={false}
            data={routePinsGeoJSON} />
        )}

        {isMapReady && routeStartEndPinsGeoJSON && (
          <MapStartEndLocationSourceAndLayers
            id="start-end-locations-source"
            type="geojson"
            data={routeStartEndPinsGeoJSON} />
        )}

        {isMapReady && searchResultPinsGeoJSON && (
          <>
            <SearchResultPinsSourceAndLayers
              id="search-result"
              type="geojson"
              data={searchResultPinsGeoJSON} />
            <SelectedPinOverlay
              id="selected-overlay-search-result"
              type="geojson"
              data={searchResultPinsGeoJSON}
              selectedPinId={selectedPinId} />
          </>
        )}

        {/* Selected pin overlay - renders on top of all other layers */}
        {isMapReady && pinsGeoJSON && (
          <SelectedPinOverlay
            id="selected-overlay"
            type="geojson"
            data={pinsGeoJSON}
            selectedPinId={selectedPinId} />
        )}
      </Map>
      <div
        className={styles.toolBox}>
        <div>
          <>
            <div>
              <div className={styles.buttonsContainer}>
                <ArrowLeftIcon
                  style={{ color: "white", cursor: 'pointer' }}
                  onClick={() => onNavigateHome()}
                  width='35px' height='35px' />
                <LocationIcon
                  style={{ color: "white", cursor: 'pointer' }}
                  onClick={centralizeMap}
                  width='35px' height='35px' />
                <LassoIcon
                  style={{
                    stroke: isLassoMode ? 'lightgreen' : 'white',
                    cursor: 'pointer'
                  }}
                  onClick={startDrawing}
                  width='35px'
                  height='35px' />
                <RoutesIcon style={{ color: "white" }}
                  onClick={() => {
                    onPinSelected(null);
                    onSlideUpMenuOpen();
                  }}
                  width='35px'
                  height='35px' />
              </div>
            </div>
          </>
        </div>
      </div>
      {isColorLabelActive && (
        <div className={styles.floatingBtnWrapper}>
          <ListBulletIcon
            fill="white"
            style={{ 
              cursor: 'pointer', 
              color: isColorLabelOnView ? "#FF9D00" : "white" 
            }}
            onClick={() => onColorLabeOnViewChange(!isColorLabelOnView)}
            width='35px'
            height='35px' />
        </div>
      )}
    </Box >
  );
});

export default MapCanvas;